import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../services/db';
import { useAuth } from '../context/AuthContext';
import { getCurrentUser } from '../services/authService';
import { AlertCircle, ArrowLeft, Loader2 } from 'lucide-react'; 

const InviteSignupPage: React.FC = () => {
  const { login, setUser } = useAuth();
 const [password, setPassword] = useState('');
 const [confirmPassword, setConfirmPassword] = useState('');
 const [loading, setLoading] = useState(false);
 const [inviteVerified, setInviteVerified] = useState(false);
 const [error, setError] = useState<string | null>(null);
 const [success, setSuccess] = useState(false);
 const [userData, setUserData] = useState<{
   email: string | null;
   name: string | null;
   company: string | null;
 }>({ email: null, name: null, company: null });
 const navigate = useNavigate();
  
 // Check if this is a custom enterprise invitation or legacy Supabase invitation
 const searchParams = new URLSearchParams(window.location.search);
 const customToken = searchParams.get('custom_token');
 const legacyTokenHash = searchParams.get('token_hash');
 const legacyType = searchParams.get('type');
 
 const isCustomInvitation = !!customToken;
 const isLegacyInvitation = !!(legacyTokenHash && legacyType === 'invite');
 
 const handleVerifyCustomInvitation = async () => {
   setLoading(true);
   setError(null);
   
   try {
     if (!customToken) {
       setError('Ungültiger Einladungslink');
       return;
     }
     
     // Call verify-enterprise-invite Edge Function
     const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/verify-enterprise-invite`, {
       method: 'POST',
       headers: {
         'Content-Type': 'application/json',
         'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
       },
       body: JSON.stringify({
         customToken: customToken
       }),
     });

     if (!response.ok) {
       const errorData = await response.json();
       setError(errorData.error || 'Fehler beim Verifizieren der Einladung');
       return;
     }

     const result = await response.json();
     
     if (!result.valid) {
       setError(result.error || 'Einladung ungültig');
       return;
     }
     
     // Set user data from verified invitation
     setUserData({
       email: result.userData.email,
       name: result.userData.name,
       company: result.userData.company
     });
     setInviteVerified(true);
     
   } catch (err) {
     console.error('Fehler beim Verifizieren der benutzerdefinierten Einladung:', err);
     setError('Fehler beim Verifizieren der Einladung. Bitte versuchen Sie es erneut.');
   } finally {
     setLoading(false);
   }
 };
 
 const handleVerifyLegacyInvitation = async () => {
   setLoading(true);
   setError(null);
   
   try {
     if (!legacyTokenHash || !legacyType) {
       setError('Ungültiger Einladungslink');
       return;
     }
     
     // Import EmailOtpType dynamically for legacy invitations
     const { EmailOtpType } = await import('@supabase/supabase-js');
     
     const {
        data: { session },
        error,
      } = await supabase.auth.verifyOtp({
        token_hash: legacyTokenHash,
        type: legacyType as EmailOtpType
      });
      
      if (error) throw error;
      if (!session) throw new Error('Keine gültige Sitzung');
      
      const user = session.user;
      if (!user) throw new Error('Kein Benutzer gefunden');
      
      setUserData({
        email: user.email,
        name: user.user_metadata?.name || null,
        company: user.user_metadata?.company || null,
      });
      
      setInviteVerified(true);
   } catch (err) {
     console.error('Fehler beim Verifizieren der Einladung:', err);
     setError('Fehler beim Verifizieren der Einladung. Bitte erneut versuchen.');
   } finally {
     setLoading(false);
   }
 };
  
 const handleVerifyInvitation = () => {
   if (isCustomInvitation) {
     handleVerifyCustomInvitation();
   } else if (isLegacyInvitation) {
     handleVerifyLegacyInvitation();
   } else {
     setError('Ungültiger Einladungslink');
   }
 };

 const handleSubmit = async (e: React.FormEvent) => {
   e.preventDefault();
   
   if (password !== confirmPassword) {
     setError('Passwörter stimmen nicht überein');
     return;
   }
   
   if (password.length < 8) {
     setError('Passwort muss mindestens 8 Zeichen lang sein');
     return;
   }
   
   setLoading(true);
   setError(null);
   
   try {
     if (isCustomInvitation) {
       // Custom enterprise invitation flow
       const signupResponse = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/complete-enterprise-signup`, {
         method: 'POST',
         headers: {
           'Content-Type': 'application/json',
           'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
         },
         body: JSON.stringify({
          customToken: customToken,
           password: password,
         }),
       });

       if (!signupResponse.ok) {
         const errorData = await signupResponse.json();
         throw new Error(errorData.error || 'Fehler bei der Registrierung');
       }

       const signupResult = await signupResponse.json();
       console.log('Enterprise signup successful:', signupResult);

       // Use AuthContext login to establish session and update user state
       console.log('Signing in enterprise user with AuthContext...');
       await login(userData.email!, password);
       console.log('Successfully signed in enterprise user via AuthContext');

    } else {
      // Legacy invitation flow
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user || !userData.name || !userData.company) {
        throw new Error('Fehlende Benutzerdaten');
      }
      
      // Update user with password and metadata
      const { error: updateError } = await supabase.auth.updateUser({
        password,
        data: {
          name: userData.name,
          company: userData.company,
        }
      });
      
      if (updateError) throw updateError;
      
      // Insert into users table
      const { error: insertError } = await supabase
        .from('users')
        .insert({
          id: user.id,
          email: user.email!,
          name: userData.name,
          company: userData.company,
          course_progress: { modulesCompleted: {}, allCompleted: false },
          completed_modules: [],
        });
      
      if (insertError) throw insertError;
      
      // Update AuthContext with fresh user data
      const freshUser = await getCurrentUser();
      if (freshUser) {
        setUser(freshUser);
      }
    }
    
    setSuccess(true);
    setTimeout(() => navigate('/kurszugang'), 2000);
    
  } finally {
    setLoading(false);
  }
};

 // Show confirmation button if token hasn't been verified yet
 if (!inviteVerified) {
   return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-md mx-auto mb-8">
            <button
              onClick={() => navigate('/login')}
              className="flex items-center text-gray-600 hover:text-gray-800"
            >
              <ArrowLeft className="h-5 w-5 mr-2" />
              <span>Zurück zum Login</span>
            </button>
          </div>

          <div className="max-w-md mx-auto">
            <div className="bg-white rounded-lg shadow-md p-8">
              <h1 className="text-2xl font-bold text-gray-800 mb-6">
                Einladung bestätigen
              </h1>

              {error && (
                <div className="mb-6 p-4 bg-red-50 text-red-700 rounded-md flex items-start">
                  <AlertCircle className="w-5 h-5 mt-0.5 mr-2 flex-shrink-0" />
                  <p>{error}</p>
                </div>
              )}

              {loading ? (
                <div className="text-center py-8">
                  <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-600" />
                  <p className="text-gray-600">Einladung wird verifiziert...</p>
                </div>
              ) : (
                <>
                  <div className="mb-6 p-4 bg-blue-50 rounded-md">
                    <p className="text-blue-800">
                      {isCustomInvitation 
                        ? 'Klicken Sie auf den Button unten, um Ihre Enterprise-Einladung zu bestätigen.'
                        : 'Klicken Sie auf den Button unten, um Ihre Einladung zu bestätigen und mit der Registrierung fortzufahren.'
                      }
                    </p>
                  </div>

                  <button
                    onClick={handleVerifyInvitation}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-4 rounded-md transition-colors"
                  >
                    Einladung bestätigen
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
   );
 }

 // Password setting form (for both custom and legacy invitations)
  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="container mx-auto px-4">
        <div className="max-w-md mx-auto mb-8">
          <button
            onClick={() => navigate('/login')}
            className="flex items-center text-gray-600 hover:text-gray-800"
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            <span>Zurück zum Login</span>
          </button>
        </div>

        <div className="max-w-md mx-auto">
          <div className="bg-white rounded-lg shadow-md p-8">
            <h1 className="text-2xl font-bold text-gray-800 mb-6">
              Registrierung abschließen
            </h1>

            {userData.name && (
              <div className="mb-6 p-4 bg-blue-50 rounded-md">
                <p className="text-blue-800">
                  Willkommen {userData.name} von {userData.company}! 
                  {isCustomInvitation 
                    ? ' Bitte setzen Sie ein Passwort für Ihren Enterprise-Account.'
                    : ' Bitte setzen Sie ein Passwort für Ihren Account.'
                  }
                </p>
              </div>
            )}

            {error && (
              <div className="mb-6 p-4 bg-red-50 text-red-700 rounded-md flex items-start">
                <AlertCircle className="w-5 h-5 mt-0.5 mr-2 flex-shrink-0" />
                <p>{error}</p>
              </div>
            )}

            {success ? (
              <div className="bg-green-50 text-green-800 p-4 rounded-md">
                <p className="mb-2 font-medium">Registrierung erfolgreich abgeschlossen!</p>
                <p>Sie werden zum Kursbereich weitergeleitet...</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                <div className="space-y-4">
                  <div>
                    <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                      Passwort
                    </label>
                    <input
                      type="password"
                      id="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                      minLength={8}
                      placeholder="Mindestens 8 Zeichen"
                    />
                  </div>

                  <div>
                    <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                      Passwort bestätigen
                    </label>
                    <input
                      type="password"
                      id="confirmPassword"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                      minLength={8}
                      placeholder="Passwort wiederholen"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className={`w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md transition-colors ${
                      loading ? 'opacity-70 cursor-not-allowed' : ''
                    }`}
                  >
                    {loading ? 'Wird gespeichert...' : 'Registrierung abschließen'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InviteSignupPage;